import { useMutation } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import { ChannelTakeawaysDocument } from './useChannelTakeaways'

const RefreshChannelTakeawaysDocument = graphql(/* GraphQL */ `
  mutation RefreshChannelTakeaways($input: RefreshChannelTakeawaysInput!) {
    refreshChannelTakeaways(input: $input) {
      ... on RefreshChannelTakeawaysSuccess {
        takeaways {
          id
          title
          description
          category
          channelIds
          feedback
          ctas {
            label
            action
            variant
          }
          metadata {
            draftId
            createPostType
            postId
            prefillAiPrompt
          }
        }
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

interface UseRefreshChannelTakeawaysOptions {
  channelId: string
  organizationId: string
}

interface UseRefreshChannelTakeawaysResult {
  refresh: () => Promise<void>
  loading: boolean
}

export function useRefreshChannelTakeaways({
  channelId,
  organizationId,
}: UseRefreshChannelTakeawaysOptions): UseRefreshChannelTakeawaysResult {
  const [mutate, { loading }] = useMutation(RefreshChannelTakeawaysDocument, {
    variables: { input: { channelId, organizationId } },
    update(cache, { data }) {
      const result = data?.refreshChannelTakeaways
      if (result?.__typename !== 'RefreshChannelTakeawaysSuccess') return
      cache.writeQuery({
        query: ChannelTakeawaysDocument,
        variables: { input: { channelId, organizationId } },
        data: { channelTakeaways: result.takeaways },
      })
    },
  })

  const refresh = async (): Promise<void> => {
    try {
      const { data } = await mutate()
      const result = data?.refreshChannelTakeaways
      if (result?.__typename === 'RefreshChannelTakeawaysSuccess') {
        toast.success('Takeaways refreshed')
        return
      }
      if (result?.__typename === 'InvalidInputError') {
        toast.error(result.message)
        return
      }
      toast.error('Failed to refresh takeaways')
    } catch {
      toast.error('Failed to refresh takeaways')
    }
  }

  return { refresh, loading }
}
